import { useStateWithOptionalPersistence } from "@/hooks/useStateWithOptionalPersistence";
import { useOnOutsideClick } from "@/shared/hooks";
import FilterMenu from "@/ui-components/filter-menu";
import clsx from "clsx";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FilterMenuConfig } from "@/shared/models/filterTypes";

export type { FilterMenuConfig };

interface HeaderFilterMenusProps<
  T extends FilterMenuConfig | FilterMenuConfig[],
> {
  label: string;
  icon: React.ReactNode;
  filterMenus: T;
  onChange: T extends FilterMenuConfig[]
    ? (selections: Record<string, string[]>) => void
    : (selections: string[]) => void;

  selections?: Record<string, string[]>;
  persistKey?: string;
  footer?: React.ReactNode;
}

function isMultiple(
  filterMenus: FilterMenuConfig | FilterMenuConfig[]
): filterMenus is FilterMenuConfig[] {
  return Array.isArray(filterMenus);
}

function HeaderFilterMenus<T extends FilterMenuConfig | FilterMenuConfig[]>(
  props: HeaderFilterMenusProps<T>
) {
  const isMultipleMenus = isMultiple(props.filterMenus);
  const isControlled = props.selections !== undefined;

  // Manage state internally if no selections prop is provided (uncontrolled mode)
  const [internalState, setInternalState] = useStateWithOptionalPersistence<
    Record<string, string[]>
  >(props.persistKey, {});

  // Use controlled state if provided, otherwise use internal state
  const currentSelections = isControlled ? props.selections : internalState;
  const selectionsCount = useMemo(
    () =>
      Object.entries(currentSelections || {}).reduce(
        (acc, [, items]) => acc + items.length,
        0
      ),
    [currentSelections]
  );

  const [showFilterMenus, setShowFilterMenus] = React.useState(false);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);

  const filterMenusContainerRef = useRef<HTMLDivElement>(null);
  const filterMenusRef = useRef<HTMLDivElement>(null);

  useOnOutsideClick(filterMenusContainerRef, filterMenusRef, () =>
    setShowFilterMenus(false)
  );

  // Calculate available height when dropdown opens
  useEffect(() => {
    if (showFilterMenus && filterMenusContainerRef.current) {
      const buttonRect = filterMenusContainerRef.current.getBoundingClientRect();
      // Find the closest scrollable widget container
      const widgetContainer = filterMenusContainerRef.current.closest(
        '[class*="overflow-auto"], [class*="overflow-y-auto"], .react-grid-item'
      );

      let availableHeight: number;
      if (widgetContainer) {
        const containerRect = widgetContainer.getBoundingClientRect();
        // Calculate space from button bottom to container bottom, with some padding
        availableHeight = containerRect.bottom - buttonRect.bottom - 16;
      } else {
        // Fallback to viewport-based calculation
        availableHeight = window.innerHeight - buttonRect.bottom - 16;
      }

      // Clamp between reasonable min/max values
      setMaxHeight(Math.max(200, Math.min(700, availableHeight)));
    }
  }, [showFilterMenus]);

  const baseFilterMenus = useMemo(
    () => (isMultipleMenus ? props.filterMenus : [props.filterMenus]),
    [isMultipleMenus, props.filterMenus]
  ) as FilterMenuConfig[];

  // Calculate disabled states dynamically based on selections
  // This avoids recreating the component when selections change
  const filterMenus = useMemo(() => {
    const hasCustomFiltersSelected =
      (currentSelections?.["category-filter"]?.length || 0) > 0;
    const hasBasicFiltersSelected =
      (currentSelections?.["basic-filter"]?.length || 0) > 0;
    const hasPortfolioFiltersSelected =
      (currentSelections?.["portfolios-filter"]?.length || 0) > 0 ||
      (currentSelections?.["portfolios-filter-prebuilt"]?.length || 0) > 0;
    const hasNonCustomFiltersSelected =
      hasBasicFiltersSelected || hasPortfolioFiltersSelected;

    return baseFilterMenus.map((menu) => {
      // Apply disabled logic based on menu ID
      if (menu.id === "portfolios-filter" || menu.id === "portfolios-filter-prebuilt" || menu.id === "basic-filter") {
        // Generate helper text when disabled
        const disabledHelperText = hasCustomFiltersSelected
          ? "Disabled - clear advanced filters"
          : menu.helperText;

        return {
          ...menu,
          disabled: hasCustomFiltersSelected,
          helperText: disabledHelperText
        };
      } else if (menu.id === "category-filter") {
        // Generate helper text when disabled
        const disabledHelperText = hasNonCustomFiltersSelected
          ? "Disabled - clear basic filters"
          : menu.helperText;

        return {
          ...menu,
          disabled: hasNonCustomFiltersSelected,
          helperText: disabledHelperText,
          filterOptions: menu.filterOptions.map((option) => ({
            ...option,
            // Only allow one custom filter at a time
            disabled: hasCustomFiltersSelected
              ? !currentSelections?.["category-filter"]?.includes(
                  String(option.id)
                )
              : false,
          })),
        };
      }
      return menu;
    });
  }, [baseFilterMenus, currentSelections]);


  const handleToggle = (
    filterMenuId: string,
    filterId: string,
    isDisabled: boolean
  ) => {
    // Prevent toggling if the filter menu is disabled
    if (isDisabled) {
      return;
    }

    const currentFilterSelections = currentSelections?.[filterMenuId] || [];
    const isSelected = currentFilterSelections.includes(filterId);

    const updatedSelections = isSelected
      ? currentFilterSelections.filter((id) => id !== filterId)
      : [...currentFilterSelections, filterId];

    const newSelections = {
      ...currentSelections,
      [filterMenuId]: updatedSelections,
    };

    // Update internal state if uncontrolled
    if (!isControlled) {
      setInternalState(newSelections);
    }

    // Always call onChange to notify parent
    if (isMultipleMenus) {
      (props.onChange as (s: Record<string, string[]>) => void)(newSelections);
    } else {
      (props.onChange as (s: string[]) => void)(
        newSelections[filterMenus[0].id] || []
      );
    }
  };

  // Only set initial state on mount for uncontrolled mode
  useEffect(() => {
    if (
      !isControlled &&
      internalState &&
      Object.keys(internalState).length > 0
    ) {
      if (isMultipleMenus) {
        (props.onChange as (s: Record<string, string[]>) => void)(
          internalState
        );
      } else {
        (props.onChange as (s: string[]) => void)(
          internalState[filterMenus[0]?.id] || []
        );
      }
    }
  }, [
    filterMenus,
    internalState,
    props.onChange,
    isControlled,
    isMultipleMenus,
  ]);

  return (
    <div className="relative no-drag" ref={filterMenusContainerRef}>
      <button
        onClick={() => setShowFilterMenus((prev) => !prev)}
        className={clsx("p-1 no-drag ", {
          "bg-squawk-blue": selectionsCount > 0 && !showFilterMenus,
          "bg-transparent hover:bg-gray-200 dark:hover:bg-gray-600": selectionsCount === 0 || showFilterMenus,
        })}
      >
        <span className="sr-only">{props.label}</span>

        <div className={clsx(showFilterMenus && "text-squawk-blue")}>
          {props.icon}
        </div>
      </button>

      {showFilterMenus && (
        <div
          ref={filterMenusRef}
          className="absolute top-full right-0 mt-1 z-50 no-drag w-[280px]"
        >
          <div
            className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg flex flex-col"
            style={{ maxHeight: maxHeight ? `${maxHeight}px` : '700px' }}
          >
            <div className="divide-y divide-gray-200 dark:divide-gray-700 overflow-y-auto flex-1 min-h-0 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-gray-100 dark:scrollbar-track-gray-800">
              {filterMenus.map((filterMenu) => (
                <FilterMenu
                  key={filterMenu.id}
                  label={filterMenu.label}
                  description={filterMenu.description}
                  filters={filterMenu.filterOptions}
                  onToggle={(filterId) =>
                    handleToggle(
                      filterMenu.id,
                      String(filterId),
                      filterMenu.disabled ?? false
                    )
                  }
                  selected={currentSelections?.[filterMenu.id] || []}
                  className="!static !min-w-0 !max-w-none !w-full !rounded-none mb-0"
                  footer={filterMenu.footer}
                  disabled={filterMenu.disabled}
                  collapsible={filterMenu.collapsible}
                  defaultCollapsed={filterMenu.defaultCollapsed}
                  helperText={filterMenu.helperText}
                  showCount={filterMenu.showCount}
                  subsections={filterMenu.subsections}
                />
              ))}
            </div>
            {props.footer && (
              <div className="border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                {props.footer}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default HeaderFilterMenus;
